import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import ProfileCard from "../../components/ProfileCard";
import { useTheme } from "../../contexts/ThemeContext";
import "./Contact.css";

export const Contact = () => {
  const { isDarkMode } = useTheme();
  const [currentGreetingIndex, setCurrentGreetingIndex] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const greetings = [
    { text: "What's up?", language: "English" },
    { text: "¿Qué tal?", language: "Spanish" },
    { text: "Apa kabar?", language: "Indonesian" },
    { text: "你好吗?", language: "Chinese" },
    // { text: "E aí?", language: "Brazilian Portuguese" },
    { text: "क्या हाल है?", language: "Hindi" },
    { text: "كيف حالك", language: "Arabic" },
    { text: "Wie geht's?", language: "German" },
    { text: "Как дела?", language: "Russian" },
    { text: "어떻게 지내?", language: "Korean" },
    { text: "Como vai?", language: "Portuguese" },
    { text: "元気?", language: "Japanese" },
    { text: "Nasılsın?", language: "Turkish" },
    { text: "Ça va?", language: "French" },
    { text: "Come stai?", language: "Italian" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentGreetingIndex(
        (prevIndex) => (prevIndex + 1) % greetings.length
      );
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [greetings.length]);

  const handleScheduleAppointment = () => {
    console.log("Schedule appointment clicked");
  };

  const emailCircleRef = useRef(null);
  const emailTlRef = useRef(null);
  const callCircleRef = useRef(null);
  const callTlRef = useRef(null);

  const initPillBtn = (circleRef, tlRef) => {
    const circle = circleRef.current;
    if (!circle?.parentElement) return;
    const btn = circle.parentElement;
    const { width: w, height: h } = btn.getBoundingClientRect();
    const R = ((w * w) / 4 + h * h) / (2 * h);
    const D = Math.ceil(2 * R) + 2;
    const delta = Math.ceil(R - Math.sqrt(Math.max(0, R * R - (w * w) / 4))) + 1;
    circle.style.width = `${D}px`;
    circle.style.height = `${D}px`;
    circle.style.bottom = `-${delta}px`;
    gsap.set(circle, { xPercent: -50, scale: 0, transformOrigin: `50% ${D - delta}px` });
    const label = btn.querySelector('.contact-pill-label');
    const labelHover = btn.querySelector('.contact-pill-label-hover');
    if (label) gsap.set(label, { y: 0 });
    if (labelHover) gsap.set(labelHover, { y: Math.ceil(h + 100), opacity: 0 });
    const tl = gsap.timeline({ paused: true });
    const ease = 'power3.out';
    tl.to(circle, { scale: 1.2, xPercent: -50, duration: 2, ease, overwrite: 'auto' }, 0);
    if (label) tl.to(label, { y: -(h + 8), duration: 2, ease, overwrite: 'auto' }, 0);
    if (labelHover) tl.to(labelHover, { y: 0, opacity: 1, duration: 2, ease, overwrite: 'auto' }, 0);
    tlRef.current = tl;
  };

  useEffect(() => {
    initPillBtn(emailCircleRef, emailTlRef);
    initPillBtn(callCircleRef, callTlRef);
  }, []);

  const makeHandlers = (tlRef) => ({
    onMouseEnter: () => {
      const tl = tlRef.current;
      if (tl) tl.tweenTo(tl.duration(), { duration: 0.3, ease: 'power3.out', overwrite: 'auto' });
    },
    onMouseLeave: () => {
      const tl = tlRef.current;
      if (tl) tl.tweenTo(0, { duration: 0.2, ease: 'power3.out', overwrite: 'auto' });
    },
  });

  return (
    <div className="contact-container">
      <img
        className="contact-ornament"
        src="/ornaments/object1.png"
        alt=""
        aria-hidden="true"
      />
      <div className="contact-content">
        {/* Greeting */}
        <div className="greeting-wrapper">
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <h1
              className="greeting"
              data-cursor-text
              key={currentGreetingIndex}
              onMouseMove={e => {
                setTooltipPos({ x: e.clientX, y: e.clientY });
                setTooltipVisible(greetings[currentGreetingIndex].language !== 'English');
              }}
              onMouseLeave={() => setTooltipVisible(false)}
            >
              {greetings[currentGreetingIndex].text}
            </h1>
          </div>
          {tooltipVisible && (
            <div style={{
              position: 'fixed',
              left: tooltipPos.x + 16,
              top: tooltipPos.y - 36,
              background: '#6B8E23',
              color: '#fff',
              padding: '0.3rem 0.75rem',
              borderRadius: '20px',
              fontSize: '0.8rem',
              fontWeight: 500,
              pointerEvents: 'none',
              zIndex: 99999,
              whiteSpace: 'nowrap',
            }}>
              What's up?
            </div>
          )}
          <p className="greeting-subtext" data-cursor-text>
            I’m always open to connect; whether you’re looking to collaborate, need design advice, or exploring new challenges together. Let’s grow and build something meaningful.
          </p>
          <div className="contact-actions">
            <div className="contact-info-section">
              <a
                href="mailto:workofedwin@gmail.com"
                className="contact-btn"
                {...makeHandlers(emailTlRef)}
              >
                <span className="contact-pill-circle" ref={emailCircleRef} aria-hidden="true" />
                <span className="contact-pill-stack">
                  <span className="contact-pill-label">Drop Me an Email</span>
                  <span className="contact-pill-label-hover" aria-hidden="true">Drop Me an Email</span>
                </span>
              </a>
            </div>
            <div className="appointment-section">
              <a
                href="https://calendar.app.google/sfX32dsXPWLFDZRN9"
                target="_blank"
                rel="noopener noreferrer"
                className="appointment-btn"
                {...makeHandlers(callTlRef)}
              >
                <span className="contact-pill-circle" ref={callCircleRef} aria-hidden="true" />
                <span className="contact-pill-stack">
                  <span className="contact-pill-label">Book a Call</span>
                  <span className="contact-pill-label-hover" aria-hidden="true">Book a Call</span>
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Left: Profile Card */}
        <div className="profile-section">
          <ProfileCard
            name="Edwin Riwaldo"
            title="Product Designer"
            handle="edwinriwaldo"
            status="Available"
            contactText="Email Me"
            avatarUrl="/ed-front.png"
            iconUrl="/logo-pc.png"
            grainUrl="/grain-1.jpg"
            showUserInfo={false}
            enableTilt={true}
            enableMobileTilt={false}
            showBehindGradient={true}
            behindGradient={
              isDarkMode
                ? "radial-gradient(farthest-side circle at var(--pointer-x) var(--pointer-y),hsla(266,60%,80%,calc(var(--card-opacity)*0.3)) 4%,hsla(266,40%,70%,calc(var(--card-opacity)*0.2)) 10%,hsla(266,20%,60%,calc(var(--card-opacity)*0.1)) 50%,hsla(266,0%,60%,0) 100%),radial-gradient(35% 52% at 55% 20%,#4f46e533 0%, #073aff00 100%),radial-gradient(100% 100% at 50% 50%,#6366f144 1%,#073aff00 76%),conic-gradient(from 124deg at 50% 50%,#8b5cf633 0%,#06b6d433 40%,#06b6d433 60%,#8b5cf633 100%)"
                : "radial-gradient(farthest-side circle at var(--pointer-x) var(--pointer-y),hsla(180,60%,80%,calc(var(--card-opacity)*0.3)) 4%,hsla(180,40%,70%,calc(var(--card-opacity)*0.2)) 10%,hsla(180,20%,60%,calc(var(--card-opacity)*0.1)) 50%,hsla(0, 0%, 78%, 0.03) 100%),radial-gradient(35% 52% at 55% 20%,#9dffeb33 0%, #073aff00 100%),radial-gradient(100% 100% at 50% 50%,#10b98144 1%,#073aff00 76%),conic-gradient(from 124deg at 50% 50%,#06b6d433 0%,#10b98133 40%,#10b98133 60%,#06b6d433 100%)"
            }
            innerGradient={
              isDarkMode
                ? "linear-gradient(145deg,rgba(55, 55, 55, 0.4) 0%,rgba(20, 20, 20, 0.3) 100%)"
                : "linear-gradient(145deg,rgba(245, 245, 245, 0.76) 0%,rgba(231, 231, 231, 0.89) 100%)"
            }
            className={isDarkMode ? "dark-mode" : "light-mode"}
            onContactClick={() => (window.location.href = "/contact")}
          />
        </div>

        {/* Right: Social Links */}
        <div className="right-section">
          <div className="contact-bottom">
            <div className="social-links">
              <a
                href="https://www.linkedin.com/in/ed-riwaldo-7a1a68122/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="social-link"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect x="2" y="9" width="4" height="12"/>
                  <circle cx="4" cy="4" r="2"/>
                </svg>
                <span className="social-tooltip">Connect on LinkedIn</span>
              </a>
              <a
                href="https://dribbble.com/edriwaldo"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Dribbble"
                className="social-link"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72m2.54-15.38c-3.72 4.35-8.94 5.66-16.88 5.85m19.5 1.9c-3.5-.93-6.63-.82-8.94 0-2.58.92-5.01 2.86-7.44 6.32"/>
                </svg>
                <span className="social-tooltip">See my Dribbble shots</span>
              </a>
              <a
                href="https://www.youtube.com/@edriwaldo6349"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="social-link"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <defs>
                    <mask id="yt-play-mask">
                      <rect width="24" height="24" fill="white"/>
                      <polygon points="9.75 15.02 15.5 12 9.75 8.98" fill="black"/>
                    </mask>
                  </defs>
                  <path mask="url(#yt-play-mask)" d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.4a2.78 2.78 0 0 0 1.95-1.97A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/>
                </svg>
                <span className="social-tooltip">Watch my YouTube</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
